import { useCallback, useState } from 'react';

import {
  CandidateDetailsAction,
  getAvailableCandidateResumeAPI,
} from '@constants';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
  availableCandidatesSelectors,
  clearAvailableCandidateWriteError,
  markAvailableCandidate,
  requestAvailableCandidateInterview,
  requestAvailableCandidateLearnMore,
} from '@redux/availableCandidates';
import { CandidateFeedbackFormValues, IAvailableCandidate } from '@types';
import {
  candidateSubtitle,
  isStatusLoading,
  showPdfInTab,
  toggledMark,
} from '@utils';

export const useAvailableCandidateActions = () => {
  const dispatch = useAppDispatch();

  const openResume = useCallback(async (candidateId: string) => {
    const tab = window.open('', '_blank');

    await showPdfInTab(tab, getAvailableCandidateResumeAPI(candidateId));
  }, []);

  const { data, writeStatus, writeError } = useAppSelector(
    availableCandidatesSelectors.getAvailableCandidatesData,
  );
  const [dialog, setDialog] = useState<{
    candidate: IAvailableCandidate;
    action: CandidateDetailsAction | null;
  } | null>(null);

  const onAction = useCallback(
    (candidateId: string, action: CandidateDetailsAction) => {
      const candidate = data.find(({ id }) => id === candidateId);
      if (!candidate) return;

      if (action === CandidateDetailsAction.SKIP_CANDIDATE) {
        dispatch(
          markAvailableCandidate({
            candidateId,
            mark: toggledMark(candidate, 'skip'),
          }),
        );
        return;
      }

      dispatch(clearAvailableCandidateWriteError());
      setDialog({ candidate, action });
    },
    [data, dispatch],
  );

  const onLearnMore = useCallback(
    (candidateId: string) =>
      onAction(candidateId, CandidateDetailsAction.ASK_QUESTION),
    [onAction],
  );

  const onClose = useCallback(() => {
    dispatch(clearAvailableCandidateWriteError());
    setDialog((current) => current && { ...current, action: null });
  }, [dispatch]);

  const onSubmit = async (values: CandidateFeedbackFormValues) => {
    if (!dialog?.action) return false;

    const { payload } = await dispatch(
      requestAvailableCandidateInterview({
        candidateId: dialog.candidate.id,
        interviewSlots: values.interviewSlots ?? [],
        clientCalendar: values.clientCalendar ?? undefined,
        clientTimezone: values.clientTimezone,
        details: values.details ?? undefined,
      }),
    );

    return payload === true;
  };

  const onAskQuestion = async (text: string) => {
    if (!dialog) return false;

    const { payload } = await dispatch(
      requestAvailableCandidateLearnMore({
        candidateId: dialog.candidate.id,
        text,
      }),
    );

    return payload === true;
  };

  const subject = dialog && {
    name: dialog.candidate.name,
    subtitle: candidateSubtitle({ profile: dialog.candidate }),
    details: null,
  };

  return {
    onAction,
    onLearnMore,
    onOpenResume: openResume,
    dialogProps: {
      subject,
      action: dialog?.action ?? null,
      isLoading: isStatusLoading(writeStatus),
      error: writeError,
      interviewRequested: true,
      onClose,
      onSubmit,
    },
    questionProps: {
      subject,
      isOpen: dialog?.action === CandidateDetailsAction.ASK_QUESTION,
      isLoading: isStatusLoading(writeStatus),
      error: writeError,
      onClose,
      onSubmit: onAskQuestion,
    },
  };
};
